<script setup lang="ts">
import { nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Link2, RemoveFormatting,
  Baseline, Highlighter, Type, Ban
} from 'lucide-vue-next'
import ToolbarMenu from './ToolbarMenu.vue'
import { FONT_FAMILIES, FONT_SIZES, TEXT_COLORS, type ActiveFormats, type ToggleCommand } from '@/composables/useRichTextFormat'

/**
 * Barra de formato del correo, compartida por el compositor, las plantillas y
 * las firmas. No sabe cómo se aplica el formato: avisa qué pidió el usuario y
 * cada editor lo resuelve con su motor (execCommand o Tiptap).
 */
const props = defineProps<{
  activeFormats: ActiveFormats
  /**
   * Lee el enlace donde está el cursor y el texto seleccionado, para abrir el
   * diálogo con los campos llenos. Lo resuelve cada editor con su motor.
   */
  readLinkContext?: () => { url: string; text: string }
}>()

const emit = defineEmits<{
  toggle: [command: ToggleCommand]
  fontFamily: [value: string]
  fontSize: [px: number]
  textColor: [color: string]
  highlight: [color: string | null]
  clearFormat: []
  link: [value: { url: string; text: string }]
  unlink: []
}>()

const linkUrl = ref('')
const linkText = ref('')
const existingLink = ref(false)
const linkUrlRef = ref<HTMLInputElement | null>(null)

/** Al abrir, el diálogo se llena con lo que haya bajo el cursor. */
async function loadLink() {
  const context = props.readLinkContext?.() ?? { url: '', text: '' }
  linkUrl.value = context.url
  linkText.value = context.text
  existingLink.value = !!context.url
  await nextTick()
  linkUrlRef.value?.focus()
}

function applyLink(close: () => void) {
  if (!linkUrl.value.trim()) return
  emit('link', { url: linkUrl.value, text: linkText.value })
  close()
}

function removeLink(close: () => void) {
  emit('unlink')
  close()
}

const { t } = useI18n()

const btnClass = 'w-[30px] h-[30px] rounded-md flex items-center justify-center transition-colors'
const idleClass = 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 hover:text-brand-navy dark:hover:text-white'
const activeClass = 'bg-secondary-100 dark:bg-secondary-700 text-brand-navy dark:text-white'

const TOGGLES: { command: ToggleCommand; icon: typeof Bold; label: string }[] = [
  { command: 'bold', icon: Bold, label: 'bold' },
  { command: 'italic', icon: Italic, label: 'italic' },
  { command: 'underline', icon: Underline, label: 'underline' },
  { command: 'strike', icon: Strikethrough, label: 'strikethrough' },
]

const LISTS: { command: ToggleCommand; icon: typeof Bold; label: string }[] = [
  { command: 'bulletList', icon: List, label: 'bulletList' },
  { command: 'orderedList', icon: ListOrdered, label: 'numberedList' },
]

const ALIGNMENTS: { command: ToggleCommand; icon: typeof Bold; label: string }[] = [
  { command: 'alignLeft', icon: AlignLeft, label: 'alignLeft' },
  { command: 'alignCenter', icon: AlignCenter, label: 'alignCenter' },
  { command: 'alignRight', icon: AlignRight, label: 'alignRight' },
]
</script>

<template>
  <div class="flex items-center flex-wrap gap-0.5 py-1 border-b border-secondary-200 dark:border-secondary-700 flex-shrink-0">
    <!-- Tipo de letra -->
    <ToolbarMenu :title="t('emailComposer.toolbar.fontFamily')" menu-width="w-52">
      <template #button><Type class="w-4 h-4" /></template>
      <template #default="{ close }">
        <div class="max-h-64 overflow-y-auto py-1">
          <button
            v-for="font in FONT_FAMILIES"
            :key="font.label"
            type="button"
            class="w-full px-3 py-1.5 text-left text-sm text-brand-navy dark:text-white hover:bg-secondary-100 dark:hover:bg-secondary-700"
            :style="{ fontFamily: font.value }"
            @click="emit('fontFamily', font.value); close()"
          >
            {{ font.label }}
          </button>
        </div>
      </template>
    </ToolbarMenu>

    <!-- Tamaño -->
    <ToolbarMenu :title="t('emailComposer.toolbar.fontSize')" menu-width="w-24">
      <template #button><span class="text-[12px] font-semibold px-0.5">px</span></template>
      <template #default="{ close }">
        <div class="max-h-64 overflow-y-auto py-1">
          <button
            v-for="size in FONT_SIZES"
            :key="size"
            type="button"
            class="w-full px-3 py-1.5 text-left text-sm text-brand-navy dark:text-white hover:bg-secondary-100 dark:hover:bg-secondary-700"
            @click="emit('fontSize', size); close()"
          >
            {{ size }}
          </button>
        </div>
      </template>
    </ToolbarMenu>

    <div class="w-px h-[18px] bg-secondary-200 dark:bg-secondary-700 mx-1"></div>

    <!-- Negrilla, cursiva, subrayado, tachado -->
    <button
      v-for="item in TOGGLES"
      :key="item.command"
      type="button"
      :class="[btnClass, activeFormats[item.command] ? activeClass : idleClass]"
      :title="t(`emailComposer.toolbar.${item.label}`)"
      @mousedown.prevent
      @click="emit('toggle', item.command)"
    >
      <component :is="item.icon" class="w-4 h-4" />
    </button>

    <div class="w-px h-[18px] bg-secondary-200 dark:bg-secondary-700 mx-1"></div>

    <!-- Color de letra -->
    <ToolbarMenu :title="t('emailComposer.toolbar.textColor')" menu-width="w-auto" :show-chevron="false">
      <template #button><Baseline class="w-4 h-4" /></template>
      <template #default="{ close }">
        <div class="grid grid-cols-6 gap-1 p-2">
          <button
            v-for="color in TEXT_COLORS"
            :key="color"
            type="button"
            class="w-5 h-5 rounded border border-secondary-300 dark:border-secondary-600"
            :style="{ backgroundColor: color }"
            :title="color"
            @click="emit('textColor', color); close()"
          ></button>
        </div>
      </template>
    </ToolbarMenu>

    <!-- Resaltado -->
    <ToolbarMenu :title="t('emailComposer.toolbar.highlight')" menu-width="w-auto" :show-chevron="false">
      <template #button><Highlighter class="w-4 h-4" /></template>
      <template #default="{ close }">
        <div class="grid grid-cols-6 gap-1 p-2">
          <button
            v-for="color in TEXT_COLORS"
            :key="color"
            type="button"
            class="w-5 h-5 rounded border border-secondary-300 dark:border-secondary-600"
            :style="{ backgroundColor: color }"
            :title="color"
            @click="emit('highlight', color); close()"
          ></button>
        </div>
        <button
          type="button"
          class="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-secondary-600 dark:text-secondary-300 border-t border-secondary-100 dark:border-secondary-700 hover:bg-secondary-100 dark:hover:bg-secondary-700"
          @click="emit('highlight', null); close()"
        >
          <Ban class="w-3.5 h-3.5" />
          {{ t('emailComposer.toolbar.noHighlight') }}
        </button>
      </template>
    </ToolbarMenu>

    <div class="w-px h-[18px] bg-secondary-200 dark:bg-secondary-700 mx-1"></div>

    <!-- Viñetas y numeración -->
    <button
      v-for="item in LISTS"
      :key="item.command"
      type="button"
      :class="[btnClass, activeFormats[item.command] ? activeClass : idleClass]"
      :title="t(`emailComposer.toolbar.${item.label}`)"
      @mousedown.prevent
      @click="emit('toggle', item.command)"
    >
      <component :is="item.icon" class="w-4 h-4" />
    </button>

    <div class="w-px h-[18px] bg-secondary-200 dark:bg-secondary-700 mx-1"></div>

    <!-- Alineación -->
    <button
      v-for="item in ALIGNMENTS"
      :key="item.command"
      type="button"
      :class="[btnClass, activeFormats[item.command] ? activeClass : idleClass]"
      :title="t(`emailComposer.toolbar.${item.label}`)"
      @mousedown.prevent
      @click="emit('toggle', item.command)"
    >
      <component :is="item.icon" class="w-4 h-4" />
    </button>

    <div class="w-px h-[18px] bg-secondary-200 dark:bg-secondary-700 mx-1"></div>

    <!-- Enlace: reemplaza al prompt del navegador -->
    <ToolbarMenu
      :title="t('emailComposer.toolbar.insertLink')"
      menu-width="w-72"
      :show-chevron="false"
      :active="!!existingLink"
      allow-focus
      @open="loadLink"
    >
      <template #button><Link2 class="w-4 h-4" /></template>
      <template #default="{ close }">
        <form class="p-3 flex flex-col gap-2" @submit.prevent="applyLink(close)">
          <label class="text-[11px] font-semibold text-secondary-500 dark:text-secondary-400">
            {{ t('emailComposer.toolbar.linkText') }}
          </label>
          <input
            v-model="linkText"
            type="text"
            class="w-full h-8 px-2 rounded-md border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-sm text-brand-navy dark:text-white focus:ring-1 focus:ring-kila-mint focus:outline-none"
            :placeholder="t('emailComposer.toolbar.linkTextPlaceholder')"
          />

          <label class="text-[11px] font-semibold text-secondary-500 dark:text-secondary-400">
            {{ t('emailComposer.toolbar.linkUrl') }}
          </label>
          <input
            ref="linkUrlRef"
            v-model="linkUrl"
            type="text"
            class="w-full h-8 px-2 rounded-md border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-sm text-brand-navy dark:text-white focus:ring-1 focus:ring-kila-mint focus:outline-none"
            placeholder="https://"
          />

          <div class="flex items-center justify-between gap-2 pt-1">
            <button
              v-if="existingLink"
              type="button"
              class="text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
              @click="removeLink(close)"
            >
              {{ t('emailComposer.toolbar.removeLink') }}
            </button>
            <span v-else></span>
            <button
              type="submit"
              class="h-8 px-3 rounded-md text-xs font-bold transition"
              :class="linkUrl.trim()
                ? 'bg-kila-mint text-brand-navy hover:brightness-95'
                : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-400 dark:text-secondary-500 cursor-not-allowed'"
              :disabled="!linkUrl.trim()"
            >
              {{ t('emailComposer.toolbar.applyLink') }}
            </button>
          </div>
        </form>
      </template>
    </ToolbarMenu>
    <button
      type="button"
      :class="[btnClass, idleClass]"
      :title="t('emailComposer.toolbar.clearFormat')"
      @mousedown.prevent
      @click="emit('clearFormat')"
    >
      <RemoveFormatting class="w-4 h-4" />
    </button>

    <!-- Botones propios de cada editor (campo dinámico, imágenes, tablas...) -->
    <slot name="extra" />
  </div>
</template>
